<template>
    <div>
        <page-header>
            Groups
            <template v-if="props.overview" v-slot:actions>
                <vx-button @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-light"
                    size="sm">
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon widthAuto class="inline-block! sm:hidden!"
                        :icon="faCircleQuestion"></font-awesome-icon>
                    <font-awesome-icon widthAuto class="hidden! sm:inline-block!"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <groups-overview v-if="props.overview" v-show="showOverview" class="mb-14"></groups-overview>

        <group-controls v-model="fetchParams"
            :teamId="props.teamId"
            :newGroupHref="props.newGroupHref"
            @apply="applyFilters"
            @reset="resetFilters"></group-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <vx-box color="info"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                No Groups yet.
                <vx-button v-if="props.newGroupHref || isAdminOrOwner"
                    data-test="new-group"
                    :href="props.newGroupHref || `/settings/teams/${props.teamId}/groups/new`">
                    New Group
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </vx-box>
        </template>

        <vx-table v-else-if="list.length"
            :class="FEATURES.group_statuses ? 'xl:grid-cols-6-auto' : 'xl:grid-cols-5-auto'"
            rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>
                    Name
                </div>
                <div v-if="FEATURES.group_statuses">
                    Status
                </div>
                <div>
                    External ID
                </div>
                <div>
                    Users
                </div>
                <div>
                    OU
                </div>
            </vx-table-row>

            <group-row v-for="(item, index) in list"
                :modelValue="list[index]"
                :key="list[index].group_id"
                :editGroupHref="props.editGroupHref"
                @deleted="removeFromList($event)"></group-row>
        </vx-table>

        <div v-if="list.length && fetchParams.cursor"
            class="mt-8">
            <vx-button @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading">
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
        </div>
    </div>
</template>

<script setup>
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faCirclePlus,
    faCircleQuestion,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { inject, onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { groupsClient } from '~/services';
import GroupControls from './GroupControls.vue';
import GroupsOverview from './GroupsOverview.vue';
import GroupRow from './GroupRow.vue';

const props = defineProps({
    teamId: {
        type: Number,
        default: () => Spark.state.currentTeam.id,
    },
    overview: {
        type: Boolean,
        default: true,
    },
    newGroupHref: {
        type: String,
        default: null,
    },
    editGroupHref: {
        type: Function,
        default: null,
    },
});

const FEATURES = inject('FEATURES');

const showOverview = ref(true);

const isAdminOrOwner = Spark.isAdminOrOwner;

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => ({
    per_page: 10,
    cursor: null,
    sort_field: 'name',
    sort_dir: 'asc',
    query: '',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    }

    loadData();
}

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    fetchRecords(groupsClient.getGroups({ teamId: props.teamId }, { ...fetchParams.value }));
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.group_id !== itemId);

    if (!list.value.length) {
        loadData();
    }
};

</script>
